import { useState } from 'react'
import { Controller, type Control, type FieldErrors, type UseFormRegister } from 'react-hook-form'
import { X } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import type { DraftEditFormData } from '@/lib/constants/draft'

interface DraftSeoEditorProps {
  register: UseFormRegister<DraftEditFormData>
  control: Control<DraftEditFormData>
  errors: FieldErrors<DraftEditFormData>
}

export function DraftSeoEditor({ register, control, errors }: DraftSeoEditorProps) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium">SEO Metadata</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Title</label>
          <Input {...register('seo.title')} placeholder="SEO title..." />
          {errors.seo?.title && (
            <p className="text-sm text-destructive mt-1">{errors.seo.title.message}</p>
          )}
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Description</label>
          <Textarea {...register('seo.description')} placeholder="SEO description..." rows={2} />
          {errors.seo?.description && (
            <p className="text-sm text-destructive mt-1">{errors.seo.description.message}</p>
          )}
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Keywords</label>
          <Controller
            name="seo.keywords"
            control={control}
            render={({ field }) => (
              <KeywordTagInput value={field.value} onChange={field.onChange} />
            )}
          />
          {errors.seo?.keywords && (
            <p className="text-sm text-destructive mt-1">{errors.seo.keywords.message}</p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function KeywordTagInput({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const [input, setInput] = useState('')

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && input.trim()) {
      e.preventDefault()
      if (!value.includes(input.trim())) {
        onChange([...value, input.trim()])
      }
      setInput('')
    }
    if (e.key === 'Backspace' && !input && value.length > 0) {
      onChange(value.slice(0, -1))
    }
  }

  const handleRemove = (keyword: string) => {
    onChange(value.filter((k) => k !== keyword))
  }

  return (
    <div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2" role="list">
          {value.map((kw) => (
            <Badge
              key={kw}
              variant="secondary"
              className="cursor-pointer text-xs"
              onClick={() => handleRemove(kw)}
              role="listitem"
            >
              {kw}
              <X className="w-3 h-3 ml-1" />
            </Badge>
          ))}
        </div>
      )}
      <Input
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Add keyword..."
      />
    </div>
  )
}
